import { Interpolation, Theme, useTheme } from '@emotion/react'
import { memo, MouseEventHandler } from 'react'
import { getContrastColour } from '../../../utils'

export interface MProps {
  key: string
  position: number
  time: number
  color?: string
  label: string
  left?: boolean
  right?: boolean
  onClick: MouseEventHandler<HTMLButtonElement>
}

const Marker = (props: MProps) => {
  const theme: Theme = useTheme()
  const tooltipBgColor = theme?.colors?.secondary || '#FFF'
  const title = props.label.replace(/(<([^>]+)>)/gi, ' ')

  return (
    <button
      css={rootStyle}
      tabIndex={0}
      title={title}
      data-testid="marker-wrapper"
      style={{ left: `${props.position}%` }}
      onClick={props.onClick}
    >
      <span css={wrapperStyle}>
        <span css={markerStyle(props.color)} data-testid="marker-segment" />
        <span css={tooltipWrapperStyle} data-testid="tooltip">
          <span
            css={tooltipStyle({
              left: props.left,
              right: props.right,
              backgroundColor: tooltipBgColor,
            })}
            dangerouslySetInnerHTML={{ __html: props.label }}
          />
        </span>
      </span>
    </button>
  )
}

export default memo(Marker)

const rootStyle: Interpolation<Theme> = {
  height: '100%',
  position: 'absolute',
  width: '1px',
  zIndex: 2,
  cursor: 'pointer',
  padding: 0,
}

const wrapperStyle: Interpolation<Theme> = {
  '&&': {
    boxSizing: 'content-box',
  },
  display: 'block',
  fontSize: '12px',
  height: '100%',
  maxWidth: '3px',
  padding: '16px',
  marginTop: '-16px',
  marginLeft: '-20px',
  width: '3px',
  '&:hover': {
    '> span:first-of-type': {
      transform: 'scaleX(4)',
    },
    '> span:last-of-type': {
      display: 'block',
    },
  },
}

const markerStyle = (color: string | undefined): Interpolation<Theme> => ({
  backgroundColor: color || 'hsla(0,0%,100%,.5)',
  display: 'block',
  height: '100%',
  width: '100%',
  transform: 'scaleX(1)',
  transition: 'all .1s ease-in-out',
})

const tooltipWrapperStyle: Interpolation<Theme> = {
  position: 'relative',
  display: 'none',
}

const tooltipStyle = ({
  left,
  right,
  backgroundColor,
}: {
  left: boolean | undefined
  right: boolean | undefined
  backgroundColor: string
}): Interpolation<Theme> => ({
  backgroundColor,
  color: getContrastColour(backgroundColor),
  borderRadius: '4px',
  left: (left && '-10px') || (right && 'auto') || '50%',
  lineHeight: '1.2em',
  right: (left && 'auto') || (right && '-10px') || 'auto',
  position: 'absolute',
  padding: '8px',
  transform: `translate(${
    (left && '-4%') || (right && '0%') || '-50%'
  }, calc(-100% - 45px))`,
  top: '-8px',
  maxWidth: '150px',
  minWidth: '130px',
  '&::before': {
    bottom: 0,
    backgroundColor,
    content: '""',
    height: '10px',
    position: 'absolute',
    left: (left && '10px') || (right && 'auto') || '50%',
    right: (left && 'auto') || (right && '10px') || 'auto',
    transform: `translate(${
      (left && '0') || (right && '0') || '-50%'
    }, 4px) rotate(45deg)`,
    width: '10px',
    zIndex: -1,
  },
})
